// The one message a multi-select ask_user answer is sent as, and the way back.
//
// A single choice sends its option's `value` verbatim, and the model relies on
// that (the Table Analyst puts a table's exact path in `value`). Multi-select
// keeps the same contract per option: each chosen value is sent verbatim, one
// per line as "- value", so a value holding a comma or "and" — a table path
// often does — cannot be misread. One chosen value is sent alone, exactly as a
// single choice would be. Free text follows after a blank line.
//
// The sandbox tool's description (agent-runner/control/src/tools/builtin/
// ask_user.ts) tells the model this format; change both together.
//
// parseMultiSelectAnswer is the way back: replay only knows the text of the
// user message that followed the question (replayEvents markAnsweredQuestions),
// so the answered card recovers which options were chosen from that text. Live
// and replayed cards therefore render from the same input.

import type { AskUserOption } from './types.js'

const ITEM_PREFIX = '- '

export function formatMultiSelectAnswer(values: string[], freetext: string): string {
  const text = freetext.trim()
  const list = values.length === 1 ? values[0] : values.map(v => ITEM_PREFIX + v).join('\n')
  return [list, text].filter(Boolean).join('\n\n')
}

export interface ParsedMultiSelectAnswer {
  /** Chosen option values, in option order. */
  values: string[]
  /** Whatever in the message was not a chosen option (the free text). */
  rest: string
}

export function parseMultiSelectAnswer(answer: string, options: AskUserOption[]): ParsedMultiSelectAnswer {
  const known = new Set(options.map(o => o.value))
  const chosen = new Set<string>()
  const rest: string[] = []
  if (known.has(answer.trim())) {
    chosen.add(answer.trim())
  } else {
    for (const line of answer.split('\n')) {
      const item = (line.startsWith(ITEM_PREFIX) ? line.slice(ITEM_PREFIX.length) : line).trim()
      if (known.has(item)) chosen.add(item)
      else rest.push(line)
    }
  }
  return {
    values: options.map(o => o.value).filter(v => chosen.has(v)),
    rest: rest.join('\n').trim(),
  }
}

/**
 * The composer's text after a pick on a card that answers through the
 * composer (guided conversations): the pick first, then whatever the person
 * typed or dictated, with any earlier pick of the same card taken out.
 */
export function fillAnswerDraft(current: string, picked: string, options: AskUserOption[]): string {
  const { rest } = parseMultiSelectAnswer(current, options)
  return [picked, rest].filter(Boolean).join('\n\n')
}

/** "A", "A and B", "A, B and C" — for the answered card's summary line. */
export function joinNaturally(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}
